import { ISORiskDesignElement } from "@design-controls/types";
import { DataProvider, QDataTable, QIcon, QText } from "@qualio/ui-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  base: ISORiskDesignElement;
  compare: ISORiskDesignElement;
};

type Column = {
  label: string;
  base: any;
  compare: any;
};

export const ISORisk: React.FC<Props> = ({ base, compare }) => {
  const { t } = useTranslation();
  const rows: Column[] = useMemo(() => {
    const rows: Column[] = [];

    if (base.title !== compare.title) {
      rows.push({
        label: t("diffView.fields.title"),
        base: base.title,
        compare: compare.title,
      });
    }

    if (base.category?.qri !== compare.category?.qri) {
      rows.push({
        label: t("diffView.fields.category"),
        base: base.category?.label,
        compare: compare.category?.label,
      });
    }
    if (base.foreseeableUseMisuse !== compare.foreseeableUseMisuse) {
      rows.push({
        label: t("diffView.fields.reasonableForeseeableUseMisuse"),
        base: base.foreseeableUseMisuse,
        compare: compare.foreseeableUseMisuse,
      });
    }
    if (base.harm !== compare.harm) {
      rows.push({
        label: t("diffView.fields.harm"),
        base: base.harm,
        compare: compare.harm,
      });
    }
    if (base.hazard !== compare.hazard) {
      rows.push({
        label: t("diffView.fields.hazard"),
        base: base.hazard,
        compare: compare.hazard,
      });
    }
    if (base.hazardSituation !== compare.hazardSituation) {
      rows.push({
        label: t("diffView.fields.hazardousSituation"),
        base: base.hazardSituation,
        compare: compare.hazardSituation,
      });
    }
    if (
      base.preMitigation?.probability !== compare.preMitigation?.probability
    ) {
      rows.push({
        label: t("diffView.fields.initialProbabilityAssessment"),
        base: base.preMitigation?.probability,
        compare: compare.preMitigation?.probability,
      });
    }
    if (base.preMitigation?.severity !== compare.preMitigation?.severity) {
      rows.push({
        label: t("diffView.fields.initialSeverityAssessment"),
        base: base.preMitigation?.severity,
        compare: compare.preMitigation?.severity,
      });
    }
    if (base.riskControl?.mitigation !== compare.riskControl?.mitigation) {
      rows.push({
        label: t("diffView.fields.riskControlDescription"),
        base: base.riskControl?.mitigation,
        compare: compare.riskControl?.mitigation,
      });
    }
    if (
      base.postMitigation?.probability !== compare.postMitigation?.probability
    ) {
      rows.push({
        label: t("diffView.fields.finalProbabilityAssessment"),
        base: base.postMitigation?.probability,
        compare: compare.postMitigation?.probability,
      });
    }
    if (base.postMitigation?.severity !== compare.postMitigation?.severity) {
      rows.push({
        label: t("diffView.fields.finalSeverityAssessment"),
        base: base.postMitigation?.severity,
        compare: compare.postMitigation?.severity,
      });
    }

    return rows;
  }, [base, compare, t]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "label",
        header: t("diffView.table.header.label"),
        meta: {
          width: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) => (
          <QText fontWeight={600}>{row.label}</QText>
        ),
      },
      {
        accessorKey: "base",
        header: t("diffView.table.header.base"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.base ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
      {
        accessorKey: "id",
        header: t("diffView.table.header.icon"),
        meta: {
          width: "60px",
        },
        cell: () => <QIcon iconName="ArrowRight" />,
      },
      {
        accessorKey: "compare",
        header: t("diffView.table.header.compare"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.compare ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
    ];
  }, [t]);

  if (!rows) {
    return null;
  }

  return (
    <DataProvider.Fixed data={rows}>
      <QDataTable columns={columns} withHeaders={false} hideItemCount />
    </DataProvider.Fixed>
  );
};
